import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { motion } from 'framer-motion';
import PricingSelection from './PricingSelection';
import { useAuth } from 'contexts/AuthContext';

const WebinarDescription = ({
  description,
  webinar,
  pricing,
  selectedOptions,
  setSelectedOptions,
  handleDirectCheckout,
  cartItems,
  navigate,
  isValidSelection,
  getAdminQuickActions
}) => {
  const { user, isAuthenticated, token } = useAuth();
    
  return (
    <div className="max-w-8xl mx-auto px-3 sm:px-4 lg:px-8 py-6 sm:py-8">
      {/* Admin Quick Actions */}
      {getAdminQuickActions() && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto"
        >
          {getAdminQuickActions()}
        </motion.div>
      )}

      <div className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-6">
        {/* Left Side: 65% - Webinar Details */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full lg:w-[65%] bg-gradient-to-br from-white to-emerald-50 backdrop-blur-sm rounded-2xl p-6 sm:p-8 md:p-10 border-2 border-emerald-100 shadow-xl shadow-green-900/5 overflow-hidden relative"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500"></div>
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-green-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-emerald-200/20 rounded-full blur-3xl"></div>
          
          <motion.h3 
            className="text-xl font-bold text-gray-900 mb-6 flex items-center border-b border-emerald-200 pb-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <motion.div
              animate={{ rotate: [0, 5, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <Icon name="BookOpen" size={20} className="mr-3 text-green-600" />
            </motion.div>
            Webinar Details
          </motion.h3>
          
          {/* HTML Content */}
          <motion.div 
            className="webinar-content text-gray-900 leading-relaxed break-words overflow-wrap-anywhere relative z-10
                       [&>ul]:space-y-3 [&>ul]:my-6 [&>ul]:ml-0 [&>ul]:pl-0
                       [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:list-none
                       [&>ul>li]:text-gray-900 [&>ul>li]:text-sm [&>ul>li]:leading-relaxed [&>ul>li]:break-words
                       
                       [&>h1]:text-gray-900 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mt-6 [&>h1]:mb-4 [&>h1]:border-b [&>h1]:border-green-600/50 [&>h1]:pb-2 [&>h1]:break-words
                       [&>h2]:text-gray-900 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mt-5 [&>h2]:mb-3 [&>h2]:break-words
                       [&>h3]:text-gray-900 [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:mt-4 [&>h3]:mb-2 [&>h3]:break-words
                       [&>h4]:text-gray-800 [&>h4]:text-base [&>h4]:font-semibold [&>h4]:mt-3 [&>h4]:mb-2 [&>h4]:break-words
                       [&>p]:text-gray-900 [&>p]:mb-4 [&>p]:leading-relaxed [&>p]:text-sm [&>p]:break-words
                       [&>strong]:text-gray-900 [&>strong]:text-2xl [&>strong]:font-bold [&>strong]:break-words
                       [&>b]:text-gray-900 [&>b]:font-bold [&>b]:break-words
                       [&>em]:text-emerald-700 [&>em]:italic [&>em]:break-words
                       [&>a]:text-green-600 [&>a]:underline [&>a]:font-medium hover:[&>a]:text-green-800 [&>a]:break-all
                       [&>blockquote]:border-l-4 [&>blockquote]:border-green-600 [&>blockquote]:pl-6 [&>blockquote]:py-4 [&>blockquote]:my-6 [&>blockquote]:italic [&>blockquote]:text-emerald-800 [&>blockquote]:bg-green-50 [&>blockquote]:break-words
                       [&>code]:bg-emerald-50 [&>code]:text-gray-900 [&>code]:px-2 [&>code]:py-1 [&>code]:rounded [&>code]:text-xs [&>code]:border [&>code]:border-emerald-200 [&>code]:break-all
                       [&>pre]:bg-emerald-50 [&>pre]:text-gray-900 [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:overflow-x-auto [&>pre]:my-4 [&>pre]:border [&>pre]:border-emerald-200
                       [&>img]:max-w-full [&>img]:h-auto [&>img]:rounded-lg [&>img]:my-6 [&>img]:mx-auto [&>img]:shadow-lg [&>img]:border [&>img]:border-emerald-100
                       [&>table]:w-full [&>table]:bg-white [&>table]:rounded-lg [&>table]:overflow-hidden [&>table]:my-6 [&>table]:border [&>table]:border-emerald-200 [&>table]:shadow-md [&>table]:table-fixed
                       [&>th]:text-gray-900 [&>th]:font-semibold [&>th]:p-3 [&>th]:text-left [&>th]:bg-green-50 [&>th]:border-b [&>th]:border-emerald-200 [&>th]:break-words
                       [&>td]:p-3 [&>td]:text-gray-900 [&>td]:border-b [&>td]:border-emerald-100 [&>td]:break-words
                       [&>hr]:border-0 [&>hr]:h-px [&>hr]:bg-gradient-to-r [&>hr]:from-transparent [&>hr]:via-green-600 [&>hr]:to-transparent [&>hr]:my-6"
            dangerouslySetInnerHTML={{ __html: description }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          />
          
          <style jsx>{`
            .webinar-content {
              word-wrap: break-word;
              overflow-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
            }
            
            .webinar-content ul {
              margin: 24px 0;
              padding: 0;
              list-style: none;
              display: flex;
              flex-direction: column;
              gap: 12px;
            }
            
            .webinar-content ul li {
              display: flex;
              align-items: flex-start;
              list-style: none;
              color: rgb(17, 24, 39);
              font-size: 14px;
              line-height: 1.6;
              font-weight: 500;
              word-wrap: break-word;
              overflow-wrap: break-word;
              position: relative;
              padding-left: 10px;
            }
            
            .webinar-content ul li::before {
              content: '✓';
              display: flex;
              align-items: center;
              justify-content: center;
              width: 20px;
              height: 20px;
              background: linear-gradient(to right, #10b981, #059669);
              border-radius: 50%;
              color: white;
              font-size: 11px;
              font-weight: bold;
              margin-right: 12px;
              margin-top: 2px;
              flex-shrink: 0;
              box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
              position: absolute;
              left: -10px;
            }
            
            @media (min-width: 640px) {
              .webinar-content ul li::before {
                width: 24px;
                height: 24px;
                margin-right: 16px;
                font-size: 12px;
              }
            }
            
            .webinar-content h1 {
              color: rgb(17, 24, 39);
              font-size: 1.5rem;
              font-weight: bold;
              margin: 1.5rem 0 1rem 0;
              padding-bottom: 0.5rem;
              border-bottom: 2px solid rgba(16, 185, 129, 0.5);
              word-wrap: break-word;
              overflow-wrap: break-word;
              position: relative;
            }
            
            .webinar-content h1::after {
              content: '';
              position: absolute;
              bottom: -2px;
              left: 0;
              width: 60px;
              height: 2px;
              background: linear-gradient(to right, #10b981, #059669);
            }
            
            .webinar-content em {
              color: rgb(4, 120, 87);
              font-style: italic;
              background: linear-gradient(120deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0) 100%);
              padding: 0 2px;
              border-radius: 2px;
            }
            
            .webinar-content a {
              color: rgb(5, 150, 105);
              text-decoration: none;
              font-weight: 600;
              word-break: break-all;
              overflow-wrap: break-word;
              position: relative;
              transition: all 0.3s ease;
              padding: 0 2px;
              border-radius: 2px;
            }
            
            .webinar-content a:hover {
              color: rgb(6, 95, 70);
              background: linear-gradient(120deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.3) 100%);
            }
            
            .webinar-content a::after {
              content: '';
              position: absolute;
              bottom: -1px;
              left: 0;
              width: 100%;
              height: 1px;
              background: linear-gradient(to right, #10b981, #059669);
              transform: scaleX(0);
              transform-origin: right;
              transition: transform 0.3s ease;
            }
            
            .webinar-content a:hover::after {
              transform: scaleX(1);
              transform-origin: left;
            }
            
            .webinar-content blockquote {
              border-left: 4px solid rgb(5, 150, 105);
              background: linear-gradient(120deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.1) 100%);
              padding: 1.5rem;
              margin: 1.5rem 0;
              font-style: italic;
              color: rgb(4, 120, 87);
              word-wrap: break-word;
              overflow-wrap: break-word;
              border-radius: 0 8px 8px 0;
              position: relative;
              overflow: hidden;
            }
            
            .webinar-content blockquote::before {
              content: '"';
              position: absolute;
              top: -10px;
              left: 10px;
              font-size: 60px;
              color: rgba(5, 150, 105, 0.1);
              font-family: Georgia, serif;
            }
            
            .webinar-content code {
              background: linear-gradient(120deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.2) 100%);
              border: 1px solid rgba(16, 185, 129, 0.3);
              font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            }
            
            .webinar-content pre {
              background: linear-gradient(120deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.1) 100%);
              border: 1px solid rgba(16, 185, 129, 0.2);
              position: relative;
            }
            
            .webinar-content pre::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              width: 4px;
              height: 100%;
              background: linear-gradient(to bottom, #10b981, #059669);
              border-radius: 4px 0 0 4px;
            }
            
            .webinar-content img {
              border: 1px solid rgba(16, 185, 129, 0.2);
              box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
              transition: transform 0.3s ease, box-shadow 0.3s ease;
            }
            
            .webinar-content img:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(16, 185, 129, 0.25);
            }
            
            .webinar-content table {
              border: 1px solid rgba(16, 185, 129, 0.2);
              box-shadow: 0 4px 6px rgba(16, 185, 129, 0.1);
            }
            
            .webinar-content th {
              background: linear-gradient(120deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.15) 100%);
            }
            
            .webinar-content tr:hover {
              background: rgba(16, 185, 129, 0.05);
            }
            
            .webinar-content hr {
              height: 2px;
              background: linear-gradient(to right, transparent, #10b981, transparent);
              border: none;
              margin: 2rem 0;
              position: relative;
            }
            
            .webinar-content hr::before {
              content: '◆';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              color: #10b981;
              background: white;
              padding: 0 10px;
              font-size: 12px;
            }
          `}</style>
        </motion.div>

        {/* Right Side: 35% - Pricing Selection */}
        <motion.div 
          className="w-full lg:w-[35%]"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="lg:top-6">
            <PricingSelection
              webinar={webinar}
              pricing={webinar.pricing}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              handleDirectCheckout={handleDirectCheckout}
              cartItems={cartItems}
              navigate={navigate}
              isValidSelection={isValidSelection}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WebinarDescription;